import { useCallback, useEffect, useRef, useState } from "react";

const paths = { options: "/stock-options/orb", swing: "/equity-swing" };
async function request(path, body) {
  const response = await fetch(`/api${path}`, body === undefined ? { cache: "no-store" } : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.detail || "Unable to load client strategy settings");
  return data;
}

export default function useClientEquityPreferences(active) {
  const [settings, setSettings] = useState({ options: null, swing: null });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState("");
  const revision = useRef(0);
  useEffect(() => {
    if (!active) return undefined;
    let mounted = true, timer;
    const poll = async () => {
      const version = revision.current;
      const results = await Promise.allSettled(Object.values(paths).map(path => request(path)));
      if (mounted && version === revision.current) Object.keys(paths).forEach((key, i) => {
        const result = results[i];
        if (result.status === "fulfilled") {
          setSettings(current => ({ ...current, [key]: result.value }));
          setErrors(current => ({ ...current, [key]: "" }));
        } else setErrors(current => ({ ...current, [key]: result.reason.message }));
      });
      if (mounted) timer = setTimeout(poll, 5000);
    };
    poll();
    return () => { mounted = false; clearTimeout(timer); };
  }, [active]);
  const update = useCallback(async (kind, clientId, values) => {
    revision.current += 1;
    setBusy(`${kind}-${clientId}`);
    try {
      const path = kind === "options" ? `${paths.options}/clients/${encodeURIComponent(clientId)}` : `${paths.swing}/clients/${encodeURIComponent(clientId)}`;
      const data = await request(path, kind === "options" ? { ...values, lots: 1 } : values);
      revision.current += 1;
      setSettings(current => ({ ...current, [kind]: data }));
      setErrors(current => ({ ...current, [kind]: "" }));
      return true;
    } catch (err) {
      revision.current += 1;
      setErrors(current => ({ ...current, [kind]: err.message }));
      return false;
    } finally { setBusy(""); }
  }, []);
  return { ...settings, errors, busy, update };
}
